import { Box, Chip, IconButton, Theme, Tooltip, useMediaQuery } from "@mui/material";
import { useEffect, useState } from "react";
import {
    List,
    Datagrid,
    TextField,
    TextInput,
    SelectInput,
    TopToolbar,
    CreateButton,
    EditButton,
    DateField,
    FunctionField,
    useRecordContext,
    useNotify,
    useDataProvider,
    Button,
    useRefresh,
    useListContext
} from "react-admin";
import DownloadIcon from '@mui/icons-material/Download';
import BlockIcon from '@mui/icons-material/Block'; // 禁用图标
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // 启用图标
import DeleteIcon from '@mui/icons-material/Delete'; // 删除图标
import PasswordVerifiers from "../deploy/passwordVerifiers";


export const StatusToggleButton = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh(); // 获取刷新函数
    const [loading, setLoading] = useState(false);
    const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

    // 根据状态决定显示禁用还是启用按钮
    const isActive = record.status === 1;

    const handleStopTraffic = async () => {
        setPasswordDialogOpen(true);
    };

    const handleClosePasswordDialog = () => {
        setPasswordDialogOpen(false);
    };

    const handleToggleStatus = () => {
        setLoading(true);
        const method = isActive ? 'disable' : 'enable';
        const successMessage = isActive ? '用户已禁用' : '用户已启用';

        dataProvider[method]('deviceAccount', { id: record.id })
            .then(() => {
                notify(successMessage, { type: 'success' });
                refresh(); // 刷新列表数据
                handleClosePasswordDialog()
            })
            .catch(error => {
                notify(`${isActive ? '禁用' : '启用'}失败: ${error.message}`, { type: 'error' });
            })
            .finally(() => setLoading(false));
    };


    // 小屏幕使用图标按钮
    if (isSmallScreen) {
        return (
            <>
                <Tooltip title={isActive ? "禁用" : "启用"} arrow>
                    <IconButton
                        onClick={handleStopTraffic}
                        disabled={loading}
                        color={isActive ? "warning" : "success"}
                        size="small"
                    >
                        {isActive ? <BlockIcon /> : <CheckCircleIcon />}
                    </IconButton>
                </Tooltip>
                <PasswordVerifiers
                    open={passwordDialogOpen}
                    onClose={handleClosePasswordDialog}
                    onConfirm={handleToggleStatus}
                    confirmLabel={isActive ? "禁用" : "启用"}
                />
            </>
        );
    }

    return (
        <>
            <Button
                label={isActive ? "禁用" : "启用"}
                onClick={handleStopTraffic}
                disabled={loading}
                color={isActive ? "warning" : "success"}
                startIcon={isActive ? <BlockIcon /> : <CheckCircleIcon />}
                sx={{
                    fontSize: '14px',
                    padding: '6px 12px',
                    minWidth: 'auto',
                }}
            />
            <PasswordVerifiers
                open={passwordDialogOpen}
                onClose={handleClosePasswordDialog}
                onConfirm={handleToggleStatus}
                confirmLabel={isActive ? "禁用" : "启用"}
            />
        </>
    );
};


export const DeleteWithConfirmButton = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const [loading, setLoading] = useState(false);
    const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

    const handleDeleteClick = () => {
        setPasswordDialogOpen(true);
    };

    const handleClosePasswordDialog = () => {
        setPasswordDialogOpen(false);
    };

    const handleDelete = () => {
        setLoading(true);
        dataProvider.delete('deviceAccount', { id: record.id })
            .then(() => {
                notify(`账号 ${record.name} 已删除`, { type: 'success' });
                refresh();
            })
            .catch(error => {
                notify(`删除失败: ${error.message}`, { type: 'error' });
            })
            .finally(() => setLoading(false));
    };

    // 小屏幕使用图标按钮
    if (isSmallScreen) {
        return (
            <>
                <Tooltip title="删除" arrow>
                    <IconButton
                        onClick={handleDeleteClick}
                        disabled={loading}
                        color="error"
                        size="small"
                    >
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
                <PasswordVerifiers
                    open={passwordDialogOpen}
                    onClose={handleClosePasswordDialog}
                    onConfirm={handleDelete}
                />
            </>
        );
    }

    // 大屏幕使用带图标的文字按钮
    return (
        <>
            <Button
                label="删除"
                onClick={handleDeleteClick}
                disabled={loading}
                color="error"
                startIcon={<DeleteIcon />}
                sx={{
                    fontSize: '14px',
                    padding: '6px 12px',
                    minWidth: 'auto',
                }}
            />
            <PasswordVerifiers
                open={passwordDialogOpen}
                onClose={handleClosePasswordDialog}
                onConfirm={handleDelete}
            />
        </>
    );
};

const ListActions = () => {
    const { setFilters, filterValues } = useListContext();  // 获取列表上下文
    const initialFilters = {};  // 初始过滤器值
    const role = localStorage.getItem("permissions");
    const dataProvider = useDataProvider(); // 获取dataProvider
    const notify = useNotify(); // 获取通知方法

    // 重置过滤器函数
    const handleResetFilters = () => {
        setFilters(initialFilters);
    };

    const rxportClick = () => {
        if (window.confirm(`确定导出销售客户信息？`)) {
            const formData = {
                customerTypeId: filterValues.customerTypeId || '',
                name: filterValues.name || '',
                contactPhone: filterValues.contactPhone || ''
            };
            dataProvider.salesCustomersExport(formData)
                .then(() => {
                    notify('导出销售客户信息成功', { type: 'success' });
                })
                .catch((error: any) => {
                    notify(error.message || '导出销售客户信息失败', { type: 'error' });
                });
        }
    }

    return (
        <TopToolbar>
            <Button
                label="重置搜索"
                onClick={handleResetFilters}
                disabled={Object.keys(filterValues).length === 0}  // 当没有过滤条件时禁用
            />
            {role != 'ops' ? <>
                <CreateButton />
                <Button
                    size="small"
                    label="导出"
                    component="a"
                    onClick={rxportClick}
                    download="导出设备销售列表.xlsx"
                    startIcon={<DownloadIcon />}
                    sx={{ ml: 1 }}
                >
                </Button></> : null}

        </TopToolbar>
    );
};



export const DeviceAccountList = () => {
    const dataProvider = useDataProvider(); // 获取dataProvider
    const [customerTypeChoices, setCustomerTypeChoices] = useState([]);

    useEffect(() => {
        // 异步获取客户类型数据
        const fetchCustomerTypes = async () => {
            try {
                const types = await dataProvider.getCustomerTypes();
                setCustomerTypeChoices(types);
            } catch (error) {
                console.error("获取客户类型失败:", error);
            }
        };

        fetchCustomerTypes();
    }, []);  // 空依赖数组确保只调用一次

    let postFilters = [
        <SelectInput
            source="customerTypeId"
            alwaysOn
            label="客户类型"
            choices={customerTypeChoices}
        />,
        <TextInput label="客户名称" alwaysOn source="name" />,
        <TextInput label="联系电话" alwaysOn source="contactPhone" />
    ];

    const role = localStorage.getItem("permissions");
    return (
        <List filters={postFilters} actions={<ListActions />} empty={false}
        >
            <Datagrid bulkActionButtons={false} rowClick={false} rowStyle={(record) => ({
                backgroundColor: record.status === 0 ? 'rgba(0, 0, 0, 0.04)' : 'inherit',
                opacity: record.status === 0 ? 0.7 : 1
            })}>
                <TextField source="id" label="客户ID" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="customerTypeName" label="客户类型" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="name" label="客户名称" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                {/* <TextField source="ecommerceUsername" label="商城账号名称" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} /> */}
                {/* <ImageField source="logo" label="LOGO" sx={{ display: 'flex', '& img': { maxWidth: 50, maxHeight: 50, objectFit: 'contain' } }} /> */}
                <TextField source="manager" label="负责人" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="contactPhone" label="联系电话" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <FunctionField
                    label="合同状态"
                    render={(record) => {
                        if (!record.contractEndDate) {
                            return null;
                        }

                        const endTime = new Date(record.contractEndDate);
                        const now = new Date();
                        // 比较日期（忽略时间部分）
                        const endDate = new Date(endTime.getFullYear(), endTime.getMonth(), endTime.getDate());
                        const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                        const isActive = endDate >= currentDate;
                        return (
                            <Chip
                                label={isActive ? "正常" : "失效"}
                                color={isActive ? "success" : 'error'}
                                size="small"
                            />
                        );
                    }}
                    sortable={false}
                />
                <DateField source="createTime" label="创建日期" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                {
                    role != 'ops' ? <FunctionField
                        label="操作"
                        render={(record) => (
                            <Box sx={{ justifyContent: 'center', display: 'flex',gap: '8px' }}>
                                <EditButton />
                                <StatusToggleButton />
                                <DeleteWithConfirmButton />
                            </Box>
                        )}
                    /> : null
                }

            </Datagrid>
        </List>
    )
}